import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PatternFormat } from 'react-number-format';
import { toast } from 'react-toastify';

import { useStore } from '../../context/StoreContext';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import axios from '../../utilities/axios';
import * as API_END_POINT from '../../utilities/constant';
import { checkCheckoutEnabled } from '../../utilities/helper';
import { getAffiliateData } from '../../utilities/affiliate';
import { commissionDisclosureEnabled } from '../../utilities/complianceCopy';
import { generateCodeVerifier, generateCodeChallenge } from '../../utilities/pkce';
import { OrderFailure, readOrderError } from '../OrderFailure';
import { Button } from '../../shared/components';
import CheckoutSummary from './CheckoutSummary';
import './checkout.css';

/* ============================================================================
   CHECKOUT — ONE PAGE, TWO SCREENS, NOT TWO ROUTES
   ============================================================================
   Ported from SP-main checkout.html. Previously this app split "your details"
   and "payment" across two routes (`/checkout` → CustomerInfo,
   `/checkout/payment-details` → PaymentDetails) with a real `navigate()`
   between them. The reference does this as ONE state machine on one URL —
   its own header comment records that this was tried the other way twice
   (a per-section Continue, then a route change) and rejected both times once
   it was clickable, not as a mockup. (Screen 2 has since moved off this
   app entirely — see the SHOPDOT-2831 note at the end of this comment.)

   ⚠ EVERY FIELD ON SCREEN 1 AT ONCE, NO PER-SECTION CONTINUE. Contact and
   delivery address are both visible together; there is exactly one gate,
   moving from screen 1 to screen 2.
   ⚠ SCREEN 2's ADDRESS RECAP SHOWS THE DATA, NOT JUST A HEADING — a shopper
   scans what they typed rather than trusting a checkmark (Baymard, quoted in
   the reference).

   ⚠ COMPANY IS NOT PORTED. The reference collects it behind a "+ Add a
   company name" link; this API's place-order body has no field for it
   (PaymentDetails' `body` below, unchanged from before, sends none). Adding
   the input with nowhere for it to go would be inventing a UI promise this
   backend cannot keep — the same "invented data stays refused" rule this
   session already applied to a fabricated free-shipping threshold on the
   product page.
   ⚠ COUNTRY STAYS A REAL, EDITABLE SELECT, NOT LOCKED. The reference locks it
   to a read-only "United States" because ITS storefront ships US-only by
   product decision. This app already fetches a live country list from the
   API and has always let a shopper choose — regressing that to a hardcoded
   lock is a business call this session isn't positioned to make on the
   reference's say-so, so the working control is kept.
   ⚠ MARKETING OPT-IN IS NOW THE SELLING PARTNER'S CONSENT CHECKBOX. It is
   optional and unchecked by default, names the selling partner who operates
   the store, and is suppressed when the store has commission disclosure off.
   The compliance mapping requires the exact copy shown to be recorded with the
   consent; capturing it on the order is a backend follow-up (shopdotapp-backend).

   ⚠ SCREEN 2 (PAYMENT) NO LONGER LIVES HERE — SHOPDOT-2831. Card entry moved
   to the ShopDot checkout domain (shopdotapp-frontend `/checkout/:store_slug`).
   Submitting screen 1 now opens a PKCE checkout session via
   `/store/checkout/handoff` and redirects there; that app returns the shopper
   to `/order-confirmation?order_ids=…` or back to `/checkout`.
   ============================================================================ */

const schema = yup.object().shape({
  contactEmail: yup.string().email('Must be a valid email.').min(5, 'Please fill this field').max(254).required('Please fill this field'),
  firstName: yup.string().required('Please fill this field').matches(/^[a-zA-Z0-9]+(?:\s[a-zA-Z0-9]+)*$/, 'please enter letters only and make sure there is not a space at beginning and end.').min(1).max(50),
  lastName: yup.string().required('Please fill this field').matches(/^[a-zA-Z0-9]+(?:\s[a-zA-Z0-9]+)*$/, 'please enter letters only and make sure there is not a space at beginning and end.').min(1).max(50),
  phoneNumber: yup.string().required('Please fill this field').matches(/\d{3}-\d{3}-\d{4}/, 'Phone number must be in the format ###-###-####'),
  addressLine1: yup.string().required('Please fill this field').min(4, 'Address Line 1 must be atleast 4 characters long.').max(100),
  country: yup.object().nullable().required('Please fill this field'),
  state: yup.object().nullable().required('Please fill this field').typeError('Please fill this field'),
  city: yup.string().required('Please enter a city name'),
  zip: yup.string().nullable().required('Please fill this field').matches(/^\d{5}$/, 'Zip code should contain 5 digits'),
  emailOptIn: yup.boolean(),
});

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, customerInfo, setCustomerInfo, pageDetails, storeDetails } = useStore();
  /* ⚠ WITHOUT THIS, `.sfx` BELOW IS JUST A CLASS NAME. `--o`/`--o-ink` never
     get written onto this subtree, so every themed control (the primary
     button, in particular) silently falls back to the document-wide default
     in storefront-tokens.css (`--o: var(--accent, #e15439)`) instead of the
     partner's actual accent — the same bug the product page had before this
     session wrapped it in `.sfx` and STILL forgot this hook. The class alone
     was never enough there either; see `useStorefrontTheme`'s own comment for
     why a plain `useRef`+`useEffect` isn't a substitute. */
  const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isSubmittingRef = useRef(false);

  /* ⚠ A FAILED PAYMENT COMES BACK HERE AS `?checkout_error=<the order API's
     raw errors>&checkout_error_status=<HTTP status>`. The checkout domain can't
     retry in place (its session is spent on the order call), so it hands the
     server's answer back untouched and `readOrderError` reads it exactly as it
     would a response this page received itself — one owner for turning an
     order failure into words. The params are dropped right away so a refresh
     doesn't show the dialog again. */
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderError, setOrderError] = useState(null);
  useEffect(() => {
    if (!searchParams.has('checkout_error')) return;
    const status = Number(searchParams.get('checkout_error_status')) || undefined;
    setOrderError(readOrderError({ errors: searchParams.get('checkout_error') || undefined }, status));
    searchParams.delete('checkout_error');
    searchParams.delete('checkout_error_status');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const { register, handleSubmit, control, formState: { errors } } = useForm({
    mode: 'onChange',
    defaultValues: {
      contactEmail: customerInfo?.contactEmail,
      firstName: customerInfo?.firstName,
      lastName: customerInfo?.lastName,
      addressLine1: customerInfo?.addressLine1,
      addressLine2: customerInfo?.addressLine2,
      country: { value: 1, label: 'United States' },
      state: customerInfo?.state,
      city: customerInfo?.city,
      zip: customerInfo?.zip,
      phoneNumber: customerInfo?.phoneNumber,
      emailOptIn: customerInfo?.emailOptIn ?? false,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(API_END_POINT.STORE_COUNTRY);
        if (response?.data?.code === 200) setCountries(response.data.data);
      } catch (err) { console.error('Error fetching countries:', err); }
    })();
    (async () => {
      try {
        const response = await axios.get(API_END_POINT.STORE_STATES(1));
        if (response?.data?.code === 200) setStates(response.data.data);
      } catch (err) { console.error('Error fetching states:', err); }
    })();
  }, []);

  /* ⚠ DEDUPE ON VALUE. The states endpoint has been seen returning the same
     state twice (two identical "Wisconsin" / "WI" rows on this QA store) —
     real duplicate data, not a mapping bug. A native `<select>` needs a
     unique `key` per `<option>`, and a shopper doesn't need to be offered
     the same state twice either, so this is the one place both problems are
     solved. */
  const dedupeByValue = (list) => {
    const seen = new Set();
    return list.filter((item) => {
      if (seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });
  };

  useEffect(() => {
    setCountry(dedupeByValue((countries || []).map((v) => ({ value: v?.id, label: v?.name }))));
  }, [countries]);
  useEffect(() => {
    setState(dedupeByValue((states || []).map((v) => ({ value: v?.code, label: v?.name }))));
  }, [states]);

  /* ⚠ HANDOFF, NOT A SECOND SCREEN — SHOPDOT-2831. The code_verifier never
     goes to the backend here; only its SHA-256 challenge does. It rides to
     the checkout domain in the URL fragment (never sent to any server), and
     that app proves possession of it in the session exchange. The ref guards
     a double click from opening two sessions. */
  const onDetailsSubmit = async (data) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setCustomerInfo(data);
    setIsRedirecting(true);
    try {
      const code_verifier = generateCodeVerifier();
      const code_challenge = await generateCodeChallenge(code_verifier);

      const shipping_address = {
        address_1: data.addressLine1,
        address_2: data.addressLine2 ? data.addressLine2 : null,
        country: data.country.label,
        city: data.city,
        state: data.state.label,
        zipcode: data.zip,
      };
      const { affiliateId, affiliateName } = getAffiliateData();

      const response = await axios.post(API_END_POINT.STORE_CHECKOUT_HANDOFF, {
        device_id: localStorage.getItem('device_id'),
        code_challenge,
        code_challenge_method: 'S256',
        redirect_on_success: `${window.location.origin}/order-confirmation`,
        redirect_on_cancel: `${window.location.origin}/checkout`,
        redirect_on_failure: `${window.location.origin}/checkout`,
        customer_info: {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.contactEmail,
          phone_number: data.phoneNumber,
          shipping_address,
          is_billing_address_same: true,
          billing_address: shipping_address,
          ...(affiliateId || affiliateName ? { additional_info: { affiliateId, affiliateName } } : {}),
        },
      });

      if (response?.data?.code !== 200) {
        throw new Error('Unable to create checkout session');
      }

      const { checkout_session_id, checkout_domain, store_slug } = response.data.data;
      const domain = checkout_domain.replace(/\/$/, '');
      window.location.href = `${domain}/checkout/${store_slug}?checkout_session_id=${checkout_session_id}#cv=${code_verifier}`;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      toast.error('Unable to proceed to checkout. Please try again.', { autoClose: 3000 });
      setIsRedirecting(false);
      isSubmittingRef.current = false;
    }
  };

  const errorCount = Object.keys(errors).length;

  if (!cartItems?.length) {
    return (
      <div className="sfx" ref={sfxRef}>
        <main id="ck-main">
          <div className="co-wrap">
            <p className="co-crumb"><Link to="/viewcart">Cart</Link> / Checkout</p>
            <div className="co-empty">
              <h3>There&rsquo;s nothing to check out</h3>
              <p>Your cart is empty. Add something and you can come straight back.</p>
              <Button onClick={() => navigate('/all-products')}>Shop all products</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="sfx" ref={sfxRef}>
      <main id="ck-main">
        <div className="co-wrap">
          <p className="co-crumb"><Link to="/viewcart">Cart</Link> / Checkout</p>
          <h1 className="co-h1">Checkout</h1>
          <p className="co-step">
            Step 1 of 2 &middot; Your details
          </p>

          <div className="co-cols">
            <div className="co-form">
                <form className="co-formgrid" onSubmit={handleSubmit(onDetailsSubmit)}>
                  {errorCount > 0 && (
                    /* ⚠ NO SECTION NAMES — every field is already on screen, so
                       naming where to look would describe what's already
                       visible. It says how many and marks them, matching
                       checkout.html's `errorBanner()`. */
                    <div className="co-top-err">
                      {errorCount} {errorCount === 1 ? 'detail is' : 'details are'} still needed. The fields are marked below.
                    </div>
                  )}

                  <div className="co-sec is-open">
                    <div className="co-sechead"><h2>Contact</h2></div>
                    <div className="co-secbody">
                      <div className="co-grid">
                        <div className={`co-f${errors.contactEmail ? ' is-bad' : ''}`}>
                          <label htmlFor="contactEmail">Email<span className="req">*</span></label>
                          <input id="contactEmail" type="email" {...register('contactEmail')} placeholder="johndoe@gmail.com" autoComplete="email" />
                          {errors.contactEmail ? <div className="co-err">{errors.contactEmail.message}</div> : <div className="co-hint">Your receipt and shipping updates go here.</div>}
                        </div>
                        <div className={`co-f${errors.phoneNumber ? ' is-bad' : ''}`}>
                          <label htmlFor="phoneNumber">Phone<span className="req">*</span></label>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <PatternFormat id="phoneNumber" format="###-###-####" value={value} onChange={onChange} placeholder="XXX-XXX-XXXX" />
                            )}
                          />
                          {errors.phoneNumber && <div className="co-err">{errors.phoneNumber.message}</div>}
                        </div>
                      </div>
                      {commissionDisclosureEnabled(storeDetails) && (
                        <div className="co-consent">
                          <h3 className="co-consent-h">Stay in touch</h3>
                          <label className="co-chk co-consent-chk" htmlFor="emailOptIn">
                            <input type="checkbox" id="emailOptIn" {...register('emailOptIn')} />
                            <span>Email me about products and offers from {storeDetails?.store_name}, who operates this store.</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="co-sec is-open">
                    <div className="co-sechead"><h2>Delivery address</h2></div>
                    <div className="co-secbody">
                      <div className="co-grid">
                        <div className={`co-f is-half${errors.firstName ? ' is-bad' : ''}`}>
                          <label htmlFor="firstName">First name<span className="req">*</span></label>
                          <input id="firstName" {...register('firstName')} placeholder="Enter First Name" autoComplete="given-name" />
                          {errors.firstName && <div className="co-err">{errors.firstName.message}</div>}
                        </div>
                        <div className={`co-f is-half${errors.lastName ? ' is-bad' : ''}`}>
                          <label htmlFor="lastName">Last name<span className="req">*</span></label>
                          <input id="lastName" {...register('lastName')} placeholder="Enter Last Name" autoComplete="family-name" />
                          {errors.lastName && <div className="co-err">{errors.lastName.message}</div>}
                        </div>
                        <div className={`co-f${errors.addressLine1 ? ' is-bad' : ''}`}>
                          <label htmlFor="addressLine1">Address<span className="req">*</span></label>
                          <input id="addressLine1" {...register('addressLine1')} placeholder="Suite/Building number, floor number, street number etc..." autoComplete="address-line1" />
                          {errors.addressLine1 && <div className="co-err">{errors.addressLine1.message}</div>}
                        </div>
                        <div className="co-f">
                          <label htmlFor="addressLine2">Apartment, suite, unit <span className="co-opt">(optional)</span></label>
                          <input id="addressLine2" {...register('addressLine2')} placeholder="Other address details, if needed" autoComplete="address-line2" />
                        </div>
                        <div className={`co-f is-third${errors.city ? ' is-bad' : ''}`}>
                          <label htmlFor="city">City<span className="req">*</span></label>
                          <input id="city" {...register('city')} placeholder="Enter your city name" autoComplete="address-level2" />
                          {errors.city && <div className="co-err">{errors.city.message}</div>}
                        </div>
                        <div className={`co-f is-third${errors.state ? ' is-bad' : ''}`}>
                          <label htmlFor="state">State<span className="req">*</span></label>
                          <Controller
                            name="state"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                              <select
                                id="state"
                                ref={ref}
                                value={value?.value || ''}
                                onChange={(e) => onChange(state.find((s) => s.value === e.target.value) || null)}
                              >
                                <option value="">Select&hellip;</option>
                                {state.map((s) => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                            )}
                          />
                          {errors.state && <div className="co-err">{errors.state.message}</div>}
                        </div>
                        <div className={`co-f is-third${errors.zip ? ' is-bad' : ''}`}>
                          <label htmlFor="zip">ZIP code<span className="req">*</span></label>
                          <input id="zip" {...register('zip')} placeholder="12345" maxLength={5} autoComplete="postal-code" />
                          {errors.zip && <div className="co-err">{errors.zip.message}</div>}
                        </div>
                        <div className={`co-f${errors.country ? ' is-bad' : ''}`}>
                          <label htmlFor="country">Country<span className="req">*</span></label>
                          <Controller
                            name="country"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                              <select
                                id="country"
                                ref={ref}
                                value={value?.value || ''}
                                onChange={(e) => onChange(country.find((c) => c.value === Number(e.target.value)) || null)}
                              >
                                <option value="">Select&hellip;</option>
                                {country.map((c) => (
                                  <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                              </select>
                            )}
                          />
                          {errors.country && <div className="co-err">{errors.country.message}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="co-place">
                    <Button type="submit" disabled={checkCheckoutEnabled(cartItems) || isRedirecting}>
                      {isRedirecting ? 'Redirecting to payment…' : 'Continue to payment'}
                    </Button>
                    <Link className="co-backlink" to="/viewcart">Back to cart</Link>
                  </div>
                </form>
            </div>

            <CheckoutSummary cartItems={cartItems} />
          </div>
        </div>
      </main>

      {orderError && (
        <div className="co-overlay">
          <OrderFailure setShowError={() => setOrderError(null)} error={orderError} />
        </div>
      )}
    </div>
  );
};

export default Checkout;
